import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FinalSignIn from "./auth/FinalSignIn";
import MagicLinkLogin from "./auth/MagicLinkLogin";
import { LoaderMessage } from "./components/LoaderMessage";
import { AuthConfigProvider, getAuthConfig } from "./config/AuthConfigContext";
import { initializeFirebase } from "./firebase/firebaseService";
import ProtectRoute from "./guard/ProtectRoute";
import {
  AuthStoreProvider,
  useAuthDispatch,
  useAuthSelector,
} from "./state/AuthStoreProvider";
import type { AuthRootState } from "./state/store";
import { getCurrentUser } from "./state/thunks/currentUserThunk";
import type { AuthWrapperConfig } from "./types";

interface AuthWrapperProps {
  config: AuthWrapperConfig;
  children: React.ReactNode;
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const dispatch = useAuthDispatch();
  const hasDispatched = useRef(false);

  const { user, verifyingUser } = useAuthSelector(
    (state: AuthRootState) => state.firebaseAuthentication,
  );

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hasDispatched.current) return;
    hasDispatched.current = true;
    dispatch(getCurrentUser());
  }, [dispatch]);

  const config = getAuthConfig();
  const authPath = config?.authRedirectPath ?? "/auth";

  useEffect(() => {
    if (
      verifyingUser === "not-verified" ||
      (!user && verifyingUser !== null && verifyingUser !== "checking")
    ) {
      if (
        location.pathname !== authPath &&
        location.pathname !== "/finishSignIn"
      ) {
        navigate(authPath, { replace: true });
      }
    }

    if (
      location.pathname === authPath &&
      user &&
      verifyingUser === "verified"
    ) {
      navigate("/", { replace: true });
    }
  }, [verifyingUser, user, navigate, location.pathname, authPath]);

  if (location.pathname === "/finishSignIn") {
    return <FinalSignIn />;
  }

  if (verifyingUser === null || verifyingUser === "checking") {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoaderMessage message="Loading..." />
      </div>
    );
  }

  if (verifyingUser === "not-verified" || !user) {
    return <MagicLinkLogin />;
  }

  return <ProtectRoute>{children}</ProtectRoute>;
}

/**
 * Main AuthWrapper HOC.
 *
 * Wraps your React application with Firebase authentication.
 * When the user is not authenticated, shows the magic link login UI.
 * When authenticated, renders your app (children).
 *
 * IMPORTANT: If your app already has a BrowserRouter, place AuthWrapper
 * INSIDE it (AuthWrapper does not create its own router).
 *
 * @example
 * ```tsx
 * <BrowserRouter>
 *   <AuthWrapper config={{ firebaseConfig: {...} }}>
 *     <Routes>
 *       <Route path="/" element={<Home />} />
 *       <Route path="/settings/*" element={<SettingsPage />} />
 *     </Routes>
 *   </AuthWrapper>
 * </BrowserRouter>
 * ```
 */
export function AuthWrapper({ config, children }: AuthWrapperProps) {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    initializeFirebase(config.firebaseConfig);
    setInitialized(true);
  }, [config.firebaseConfig]);

  if (!initialized) {
    return null;
  }

  return (
    <AuthConfigProvider config={config}>
      <AuthStoreProvider>
        <AuthGate>{children}</AuthGate>
        <div id="recaptcha-container" />
      </AuthStoreProvider>
    </AuthConfigProvider>
  );
}
